import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as emailTemplateService from '../../../services/email.template';
import Breadcrums from "../../../common/breadcrumbs";
import { SWAL_SETTINGS, THEME_COLORS } from '../../../../../utils/Constants';
import { formateDateWithMonth, handleServerValidations, hasP, TrimText } from '../../../../../utils/commonfunction';
import { useTranslation } from "react-i18next";
import { CheckPicker, CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery } from 'react-query';
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import moment from "moment";
import NoRowsOverlay from '../../../common/NoRowsOverlay';

ModuleRegistry.registerModules([AllCommunityModule]);

const CATEGORY_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Basic User", value: "basic_user" },
    { label: "Parent", value: "parent" },
    { label: "Player", value: "player" },
    { label: "Club Admin", value: "clubadmin" },
    { label: "Team Manager", value: "team_manager" },
    { label: "Admin", value: "admin" },
];

export const getTemplateCategories = (row) => {
    const categories = new Set();

    const addCat = (c) => {
        if (!c) return;
        const lower = String(c).toLowerCase().trim();
        if (lower === "clubadmin" || lower === "club admin") categories.add("clubadmin");
        else if (lower === "basic_user" || lower === "basicuser" || lower === "basic user") categories.add("basic_user");
        else if (lower === "team_manager" || lower === "team manager") categories.add("team_manager");
        else if (lower === "parent") categories.add("parent");
        else if (lower === "player") categories.add("player");
        else if (lower === "admin") categories.add("admin");
        else if (lower === "all") categories.add("all");
        else categories.add(lower);
    };

    if (row.category) {
        if (Array.isArray(row.category)) {
            row.category.forEach(addCat);
        } else if (typeof row.category === 'string') {
            row.category.split(',').forEach(addCat);
        }
    }
    if (row.categories && Array.isArray(row.categories)) {
        row.categories.forEach(addCat);
    }
    if (categories.size > 0) {
        return Array.from(categories);
    }

    const title = (row.title || "");
    const slugLower = (row.slug || "").toLowerCase();

    // Extract recipient/target prefix before "Notify", "notify", "Notifiy", or "-" if present
    let prefix = title;
    const notifyIndex = title.search(/notify|notifiy|-/i);
    if (notifyIndex !== -1) {
        prefix = title.substring(0, notifyIndex);
    }
    const prefixLower = prefix.toLowerCase();

    if (prefixLower.includes("basic user") || prefixLower.includes("basic_user") || prefixLower.includes("basicuser")) categories.add("basic_user");
    if (prefixLower.includes("parent")) categories.add("parent");
    if (prefixLower.includes("player")) categories.add("player");
    if (prefixLower.includes("team manager") || prefixLower.includes("manager")) categories.add("team_manager");
    if (prefixLower.includes("club")) categories.add("clubadmin");
    if (prefixLower.includes("admin") && !prefixLower.includes("club")) categories.add("admin");
    if (prefixLower.includes("all")) categories.add("all");

    // Fallback if prefix matching found nothing
    if (categories.size === 0) {
        const titleLower = title.toLowerCase();
        if (titleLower.includes("basic user") || titleLower.includes("basic_user") || slugLower.includes("basic_user")) categories.add("basic_user");
        if (titleLower.includes("parent")) categories.add("parent");
        if (titleLower.includes("player")) categories.add("player");
        if (titleLower.includes("team manager")) categories.add("team_manager");
        if (titleLower.includes("club notify") || titleLower.includes("club admin")) categories.add("clubadmin");
        if (titleLower.includes("admin notify")) categories.add("admin");
        if (titleLower.includes("all notify")) categories.add("all");
    }

    // Secondary fallback based on slug
    if (categories.size === 0) {
        if (slugLower.includes("basic_user") || slugLower.includes("basicuser")) {
            categories.add("basic_user");
        } else if (slugLower.endsWith("_admin") || slugLower.includes("admin_request")) {
            categories.add("admin");
        } else if (slugLower.includes("manager")) {
            categories.add("team_manager");
        } else if (slugLower.includes("club") || slugLower.includes("onboarding")) {
            categories.add("clubadmin");
        } else if (slugLower.includes("parent")) {
            categories.add("parent");
        } else if (slugLower.includes("player")) {
            categories.add("player");
        } else {
            categories.add("all");
        }
    }

    return Array.from(categories);
};

const EmailTemplatesTable = () => {
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const gridRef = useRef();

    // Load initial filters from URL search params or sessionStorage
    const getInitialFilters = () => {
        const searchFromUrl = searchParams.get('search');
        const categoriesFromUrl = searchParams.get('categories');
        const startDateFromUrl = searchParams.get('startDate');
        const endDateFromUrl = searchParams.get('endDate');

        const hasUrlParams = searchFromUrl !== null || categoriesFromUrl !== null || startDateFromUrl !== null || endDateFromUrl !== null;

        if (hasUrlParams) {
            return {
                search: searchFromUrl || "",
                categories: categoriesFromUrl ? categoriesFromUrl.split(',').filter(Boolean) : [],
                dateRange: (startDateFromUrl && endDateFromUrl)
                    ? [moment(startDateFromUrl).toDate(), moment(endDateFromUrl).toDate()]
                    : null
            };
        }

        try {
            const saved = sessionStorage.getItem("email_template_filters");
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    search: parsed.search || "",
                    categories: Array.isArray(parsed.categories) ? parsed.categories : [],
                    dateRange: (parsed.startDate && parsed.endDate)
                        ? [moment(parsed.startDate).toDate(), moment(parsed.endDate).toDate()]
                        : null
                };
            }
        } catch (e) {
            console.error("Failed to read filters from sessionStorage", e);
        }

        return { search: "", categories: [], dateRange: null };
    };

    const initialFilters = useMemo(() => getInitialFilters(), []);

    const [searchData, setSearchData] = useState(initialFilters.search);
    const [selectedCategories, setSelectedCategories] = useState(initialFilters.categories);
    const [dateRange, setDateRange] = useState(initialFilters.dateRange);
    const [perPage, setPerPage] = useState(10);
    const [pageIndex] = useState(Number(params.pgno) - 1 || 0);

    const updateFilters = (newSearch, newCategories, newDateRange) => {
        const filterObj = {
            search: newSearch || "",
            categories: newCategories || [],
            startDate: (newDateRange && newDateRange[0]) ? moment(newDateRange[0]).format('YYYY-MM-DD') : null,
            endDate: (newDateRange && newDateRange[1]) ? moment(newDateRange[1]).format('YYYY-MM-DD') : null,
        };
        sessionStorage.setItem("email_template_filters", JSON.stringify(filterObj));

        const paramsObj = new URLSearchParams(location.search);
        if (newSearch) {
            paramsObj.set('search', newSearch);
        } else {
            paramsObj.delete('search');
        }

        if (newCategories && newCategories.length > 0) {
            paramsObj.set('categories', newCategories.join(','));
        } else {
            paramsObj.delete('categories');
        }

        if (newDateRange && newDateRange[0] && newDateRange[1]) {
            paramsObj.set('startDate', moment(newDateRange[0]).format('YYYY-MM-DD'));
            paramsObj.set('endDate', moment(newDateRange[1]).format('YYYY-MM-DD'));
        } else {
            paramsObj.delete('startDate');
            paramsObj.delete('endDate');
        }

        setSearchParams(paramsObj, { replace: true });
    };

    useEffect(() => {
        const searchFromUrl = searchParams.get('search');
        const categoriesFromUrl = searchParams.get('categories');
        const startDateFromUrl = searchParams.get('startDate');
        const endDateFromUrl = searchParams.get('endDate');
        const hasUrlParams = searchFromUrl !== null || categoriesFromUrl !== null || startDateFromUrl !== null || endDateFromUrl !== null;

        if (!hasUrlParams && (initialFilters.search || initialFilters.categories.length > 0 || initialFilters.dateRange)) {
            updateFilters(initialFilters.search, initialFilters.categories, initialFilters.dateRange);
        }
    }, []);

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: t("sidebar_link_email_template"), url: "" }
    ];

    // Dev mode check
    const queryParams = new URLSearchParams(location.search);
    const devParam = queryParams.get('dev');
    const showAddButton = devParam === 'true';
    const [isDevMode, setIsDevMode] = useState(showAddButton);

    useEffect(() => {
        setIsDevMode(showAddButton);
    }, [showAddButton]);

    // ================= FETCH =================
    const { data, isLoading, refetch } = useQuery({
        queryKey: ["email-template-list"],
        queryFn: () => emailTemplateService.List({
            limit: 1000, // Fetch all records for client-side pagination
        }),
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error) => {
            Swal.fire({
                icon: "error",
                text: handleServerValidations(error),
                ...SWAL_SETTINGS,
            });
        },
    });

    // ================= VIEW =================
    const viewfunction = (row) => {
        navigate(`/admin/cms/default-email-template/view/${row.originalId}`);
    }

    // ================= EDIT =================
    const goToEdit = (row) => {
        navigate(`/admin/cms/default-email-template/${params.pgno}/edit/${row.originalId}`);
    }

    // ================= DELETE =================
    function Deletefunction(data) {
        Swal.fire({
            customClass: 'swal-wide',
            title: t("msg_are_you_sure"),
            text: t("you_not_be_able_to_revert_this"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
            cancelButtonColor: "#000",
            confirmButtonText: t("btn_yes_delete"),
            cancelButtonText: t("btn_cancel")
        }).then((result) => {
            if (result.isConfirmed) {
                emailTemplateService.Delete(data.originalId).then(response => {
                    if (response.success) {
                        Swal.fire({
                            icon: 'success',
                            text: response.message,
                            ...SWAL_SETTINGS
                        });
                        refetch();
                    } else {
                        Swal.fire({
                            icon: 'error',
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS
                        });
                    }
                }).catch((error) => {
                    Swal.fire({
                        icon: 'error',
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS
                    });
                });
            }
        });
    }

    // ================= COLUMNS =================
    const colDefs = useMemo(() => [
        {
            headerName: t("list_heading_sno"),
            field: "srNo",
            maxWidth: 85,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockPosition: true,
            valueGetter: (params) => {
                if (!params.node || params.node.rowIndex === undefined) return "";
                return params.node.rowIndex + 1;
            },
            cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
        },
        {
            headerName: t("title"),
            field: "title",
            flex: 1,
            minWidth: 200,
            maxWidth: 380,
            tooltipField: "title",
            cellRenderer: (params) => {
                return (
                    <div
                        style={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {params.value || "N/A"}
                    </div>
                );
            },
        },
        {
            headerName: "Category",
            field: "categoryLabels",
            minWidth: 260,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const categories = params.data?.categories || [];
                if (!categories.length) return <span>N/A</span>;

                const getCategoryKey = (cat) => {
                    if (!cat) return "";
                    const lower = String(cat).toLowerCase().trim();
                    if (lower === "clubadmin" || lower === "club admin") return "clubadmin";
                    if (lower === "basic_user" || lower === "basicuser" || lower === "basic user") return "basic_user";
                    if (lower === "team_manager" || lower === "team manager") return "team_manager";
                    if (lower === "parent") return "parent";
                    if (lower === "player") return "player";
                    if (lower === "admin") return "admin";
                    if (lower === "all") return "all";
                    return lower;
                };

                const getBadgeClass = (cat) => {
                    const key = getCategoryKey(cat);
                    switch (key) {
                        case 'admin': return 'badge-danger-light';
                        case 'clubadmin': return 'badge-primary-light';
                        case 'parent': return 'badge-warning-light';
                        case 'player': return 'badge-success-light';
                        case 'team_manager': return 'badge-info-light';
                        case 'basic_user': return 'badge-secondary-light';
                        case 'all': return 'badge-light';
                        default: return 'badge-light';
                    }
                };

                const getLabel = (cat) => {
                    const key = getCategoryKey(cat);
                    switch (key) {
                        case 'admin': return 'Admin';
                        case 'clubadmin': return 'Club Admin';
                        case 'parent': return 'Parent';
                        case 'player': return 'Player';
                        case 'team_manager': return 'Team Manager';
                        case 'basic_user': return 'Basic User';
                        case 'all': return 'All';
                        default:
                            return String(cat)
                                .split(' ')
                                .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                .join(' ');
                    }
                };

                return (
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: "4px", height: "100%", padding: "2px 0" }}>
                        {categories.map((cat, idx) => (
                            <span
                                key={idx}
                                className={`badge badge-pill ${getBadgeClass(cat)}`}
                                style={{
                                    fontSize: "10.5px",
                                    padding: "3px 7px",
                                    lineHeight: "1.2",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                    color: cat === 'basic_user' ? '#1e293b' : undefined,
                                    backgroundColor: cat === 'basic_user' ? '#e2e8f0' : undefined,
                                }}
                            >
                                {getLabel(cat)}
                            </span>
                        ))}
                    </div>
                );
            }
        },
        {
            headerName: t("label_slug"),
            field: "slug",
            flex: 1,
            minWidth: 300,
            maxWidth: 320,
            tooltipField: "slug",
            cellRenderer: (params) => {
                return (
                    <div
                        style={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {params.value || "N/A"}
                    </div>
                );
            },
        },
        {
            headerName: t("list_heading_subject"),
            field: "subject",
            flex: 1,
            minWidth: 200,
            maxWidth: 380,
            tooltipField: "subject",
            cellRenderer: (params) => {
                return (
                    <div
                        style={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {params.value || "N/A"}
                    </div>
                );
            },

        },
        {
            headerName: t("list_heading_created_date"),
            field: "createdAt",
            filter: "agDateColumnFilter",
            minWidth: 150,
            maxWidth: 150,
            valueGetter: (params) =>
                params.data?.createdAt ? new Date(params.data.createdAt) : null,
            valueFormatter: (params) =>
                params.value ? moment(params.value).format("MMMM DD, YYYY") : ""
        },
        ...(hasP("email-templates-view") || hasP("email-templates-edit") || isDevMode ? [{
            headerName: t("list_heading_action"),
            pinned: "right",
            minWidth: 130,
            maxWidth: 130,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const row = params.data;
                return (
                    <div className="btn-toolbar table-actions">
                        <div className="btn-group btn-group-sm">
                            {hasP("email-templates-view") && (
                                <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        className="btn view"
                                        onClick={() => viewfunction(row)}
                                    >
                                        <i className="ri-eye-fill"></i>
                                    </button>
                                </Whisper>
                            )}

                            {hasP("email-templates-edit") && (
                                <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        className="btn edit"
                                        onClick={() => goToEdit(row)}
                                    >
                                        <i className="ri-pencil-fill"></i>
                                    </button>
                                </Whisper>
                            )}

                            {isDevMode && (
                                <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        className="btn delete"
                                        onClick={() => Deletefunction(row)}
                                    >
                                        <i className="ri-delete-bin-6-fill"></i>
                                    </button>
                                </Whisper>
                            )}
                        </div>
                    </div>
                );
            },
        }] : []),
    ], [t, navigate, isDevMode]);

    // ================= ROW DATA =================
    const rowData = useMemo(() => {
        if (!data?.data?.list) return [];

        return data.data.list
            .map((row, i) => {
                const categories = getTemplateCategories(row);
                return {
                    srNo: pageIndex * perPage + i + 1,
                    originalId: row.id,
                    title: row.title ? TrimText(row.title, 70) : "N/A",
                    slug: row.slug || "N/A",
                    subject: row.subject || "N/A",
                    createdAt: row.createdAt,
                    category: row.category,
                    categories: categories,
                    categoryLabels: categories.map(c => {
                        const opt = CATEGORY_OPTIONS.find(o => o.value === c);
                        return opt ? opt.label : c;
                    })
                };
            })
            .filter((row) => {
                // Category Filter Logic
                const matchesCategory =
                    selectedCategories.length === 0 ||
                    row.categories.some(cat => selectedCategories.includes(cat));

                // Search Filter Logic
                const matchesSearch = !searchData || (
                    row.title.toLowerCase().includes(searchData.toLowerCase()) ||
                    row.slug.toLowerCase().includes(searchData.toLowerCase()) ||
                    row.subject.toLowerCase().includes(searchData.toLowerCase()) ||
                    row.categoryLabels.some(lbl => lbl.toLowerCase().includes(searchData.toLowerCase()))
                );

                // Date Range Filter Logic
                const matchesDate = !dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1] || (
                    row.createdAt &&
                    moment(row.createdAt).isSameOrAfter(moment(dateRange[0]).startOf("day")) &&
                    moment(row.createdAt).isSameOrBefore(moment(dateRange[1]).endOf("day"))
                );

                return matchesCategory && matchesSearch && matchesDate;
            });
    }, [data, pageIndex, perPage, searchData, selectedCategories, dateRange]);

    // ================= AG GRID CONFIG =================
    const getAgGridConfig = useMemo(() => ({
        ref: gridRef,
        domLayout: "autoHeight",
        rowData: rowData,
        columnDefs: colDefs,
        noRowsOverlayComponent: NoRowsOverlay,
        pagination: true,
        paginationPageSize: 10,
        paginationPageSizeSelector: [10, 20, 50, 100],
        suppressCellFocus: true,
        defaultColDef: {
            resizable: true,
            sortable: true,
            minWidth: 150,
            cellStyle: {
                padding: "10px 8px",
            },
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        },
        headerHeight: 50,
        rowHeight: 60,
    }), [rowData, colDefs]);

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card overflow-hidden">
                    <div
                        className="card-header d-flex justify-content-between align-items-center"
                        style={{ borderBottom: "none" }}
                    >
                        <h6 className="main-content-label">{t("sidebar_link_email_template")}</h6>
                        <div className='d-flex align-items-center'>
                            {/* Search input */}
                            <div style={{ width: 250 }} className="me-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search..."
                                    value={searchData}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSearchData(val);
                                        updateFilters(val, selectedCategories, dateRange);
                                    }}
                                    style={{ outline: "none", boxShadow: "none" }}
                                />
                            </div>

                            {/* Category multi-select filter */}
                            <div className="me-3">
                                <CustomProvider>
                                    <CheckPicker
                                        data={CATEGORY_OPTIONS}
                                        value={selectedCategories}
                                        onChange={(val) => {
                                            let updated;
                                            if (val.includes("all") && !selectedCategories.includes("all")) {
                                                updated = ["all"];
                                            } else if (val.includes("all") && val.length > 1) {
                                                updated = val.filter(v => v !== "all");
                                            } else {
                                                updated = val;
                                            }
                                            setSelectedCategories(updated);
                                            updateFilters(searchData, updated, dateRange);
                                        }}
                                        placeholder="Category"
                                        style={{ width: "200px" }}
                                        cleanable={true}
                                        searchable={false}
                                    />
                                </CustomProvider>
                            </div>

                            {/* Date range filter */}
                            <div className="me-3">
                                <CustomProvider>
                                    <DateRangePicker
                                        style={{ width: "150px" }}
                                        placeholder={t("input_placeholder_date")}
                                        placement="bottomEnd"
                                        value={dateRange}
                                        onChange={(value) => {
                                            setDateRange(value);
                                            updateFilters(searchData, selectedCategories, value);
                                        }}
                                        cleanable={true}
                                    />
                                </CustomProvider>
                            </div>

                            {/* Add New button - dev mode */}
                            {showAddButton && (
                                <button
                                    className="btn ripple btn-main-primary signbtn"
                                    onClick={() => navigate(`/admin/cms/default-email-template/add?dev=true`)}
                                >
                                    {t("btn_add_new")}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="card-body">
                        {isLoading ? (
                            <Placeholder.Paragraph
                                className="w-100 admin-placeholder"
                                rowHeight={30}
                                rowSpacing={5}
                                active={true}
                                rows={7}
                            />
                        ) : (
                            <div className="ag-theme-alpine" style={{ width: '100%' }}>
                                <AgGridReact {...getAgGridConfig} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default EmailTemplatesTable;